import React, { useState, useMemo, useEffect } from 'react';
import { Appointment, WEEK_DAYS, AttendanceStatus, Service, SupportProvider } from '../types';
import TimeSlotSchedule from './TimeSlotSchedule';
import { getScheduleApi } from '../api/scheduleApi';
import { APP_CONFIG } from '../constants/config';
import type { AdminScheduleSlot } from '../api/appointmentApi';

interface DaySchedule {
  dayId: string;
  dayLabel: string;
  isOpen: boolean;
  workingHours: { startTime: string; endTime: string }[];
}

interface AppointmentsSummaryProps {
  appointments: Appointment[];
  searchTerm: string;
  onUpdateStatus: (id: string, status: AttendanceStatus) => void;
  services: Service[];
  initialSelectedDate?: string | null;
  onDateChange?: (date: string | null) => void;
  loading?: boolean;
  onAppointmentCreated?: () => void;
  onScheduleLoaded?: (rows: AdminScheduleSlot[], day: string, scheduleType: 'US' | 'Xray') => void;
  userName?: string;
}

const Calendar: React.FC<{
  date: Date;
  selectedDate: string | null;
  appointmentDates: Set<string>;
  onDateClick: (date: string) => void;
  usSchedule: DaySchedule[];
  xraySchedule: DaySchedule[];
}> = ({ date, selectedDate, appointmentDates, onDateClick, usSchedule, xraySchedule }) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const monthName = date.toLocaleString('he-IL', { month: 'long', year: 'numeric' });
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(<div key={`empty-${i}`} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month, day);
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isPast = currentDate.getTime() < today.getTime();
    const hasAppointment = appointmentDates.has(dateString);
    
    // Get day of week and check if services are closed
    const dayOfWeek = WEEK_DAYS[currentDate.getDay()];
    const usDay = usSchedule.find(d => d.dayId === dayOfWeek.id);
    const xrayDay = xraySchedule.find(d => d.dayId === dayOfWeek.id);
    
    // When ENABLE_XRAY is false, only check US schedule, otherwise check both
    const bothClosed = APP_CONFIG.ENABLE_XRAY 
      ? (!usDay || !usDay.isOpen) && (!xrayDay || !xrayDay.isOpen)
      : (!usDay || !usDay.isOpen);
    
    // If day is closed and has no appointments, disable it
    if (bothClosed && !hasAppointment) {
       calendarDays.push(
        <div key={dateString} className="w-9 h-9 flex items-center justify-center text-slate-400">
          {day}
        </div>
      );
      continue;
    }

    const isToday = currentDate.getTime() === today.getTime();
    const isSelected = dateString === selectedDate;

    const classes = [
      'w-9', 'h-9', 'flex', 'items-center', 'justify-center', 'rounded-full', 'transition-colors', 'duration-200', 'relative',
      isSelected ? 'bg-teal-600 text-white font-bold' : 'hover:bg-teal-100',
      isToday ? 'border-2 border-cyan-500' : '',
      !isSelected && 'text-slate-700',
      isPast && !isSelected ? 'opacity-60' : ''
    ].join(' ');

    calendarDays.push(
      <button key={dateString} onClick={() => onDateClick(dateString)} className={classes}>
        {day}
        {hasAppointment && !isSelected && <span className="absolute bottom-1 w-1 h-1 bg-teal-500 rounded-full"></span>}
      </button>
    );
  }

  return (
    <div className="p-2 bg-slate-50 rounded-lg">
      <h4 className="text-center font-bold text-slate-800 mb-2">{monthName}</h4>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 font-medium mb-2">
        {WEEK_DAYS.map(day => <div key={day.id}>{day.label.substring(0, 3)}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1 place-items-center">
        {calendarDays}
      </div>
    </div>
  );
};

const SkeletonBlock: React.FC<{ className: string }> = ({ className }) => (
  <div className={`animate-pulse rounded-md bg-slate-200 ${className}`} />
);

const AppointmentsSummarySkeleton: React.FC<{ selectedDate: string | null }> = ({ selectedDate }) => {
  if (selectedDate) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-slate-200 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <SkeletonBlock className="h-7 w-72 max-w-full" />
            <SkeletonBlock className="h-10 w-28" />
          </div>
          <div className="flex gap-2">
            <SkeletonBlock className="h-10 w-36" />
            <SkeletonBlock className="h-10 w-36" />
          </div>
          <SkeletonBlock className="h-4 w-48" />
        </div>

        <div className="overflow-x-auto p-4">
          <div className="space-y-3 min-w-[900px]">
            <div className="grid grid-cols-10 gap-3">
              {Array.from({ length: 10 }).map((_, index) => (
                <SkeletonBlock key={`header-${index}`} className="h-4 w-full" />
              ))}
            </div>
            {Array.from({ length: 7 }).map((_, rowIndex) => (
              <div key={`row-${rowIndex}`} className="grid grid-cols-10 gap-3 items-center">
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
                <SkeletonBlock className="h-9 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="space-y-4">
        <SkeletonBlock className="h-7 w-56 mx-auto" />
        <SkeletonBlock className="h-5 w-32 mx-auto" />
      </div>
    </div>
  );
};


const AppointmentsSummary: React.FC<AppointmentsSummaryProps> = ({ appointments, searchTerm, onUpdateStatus, services, initialSelectedDate, onDateChange, loading = false, onAppointmentCreated, onScheduleLoaded, userName }) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(initialSelectedDate || null);
  const [syncFilter, setSyncFilter] = useState<'All' | 'Synced' | 'NotSynced'>('All');
  const [serviceFilter, setServiceFilter] = useState<string>('All');
  const [providerFilter, setProviderFilter] = useState<SupportProvider | 'All'>('All');
  const [usSchedule, setUsSchedule] = useState<DaySchedule[]>([]);
  const [xraySchedule, setXraySchedule] = useState<DaySchedule[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  
  const [sortConfig, setSortConfig] = useState<{ key: 'patientName' | 'date'; direction: 'ascending' | 'descending' }>({ key: 'date', direction: 'ascending' });

  const getDisplayMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    return new Date(year, month, 1);
  };

  // Generate array of months to display (60 days = ~2 months)
  const getMonthsToDisplay = () => {
    const months = [];
    const baseMonth = getDisplayMonth();
    for (let i = 0; i < 3; i++) {
      months.push(new Date(baseMonth.getFullYear(), baseMonth.getMonth() + i, 1));
    }
    return months;
  };

  const monthsToDisplay = getMonthsToDisplay();
  const visibleMonths = monthsToDisplay.slice(carouselIndex, carouselIndex + 2);

  const handleNext = () => {
    if (carouselIndex < monthsToDisplay.length - 2) {
      setCarouselIndex(carouselIndex + 1);
    }
  };

  const handlePrev = () => {
    if (carouselIndex > 0) {
      setCarouselIndex(carouselIndex - 1);
    }
  };

  const displayMonth = getDisplayMonth();
  const appointmentDates = useMemo(() => new Set(appointments.map(app => app.date)), [appointments]);
  
  // Load schedules on mount
  useEffect(() => {
    const loadSchedules = async () => {
      try {
        const [us, xray] = await Promise.all([
          getScheduleApi(2), // US
          getScheduleApi(1)  // Xray
        ]);
        setUsSchedule(us);
        setXraySchedule(xray);
      } catch (error) {
        console.error('Failed to load schedules:', error);
      }
    };
    loadSchedules();
  }, []);
  
  const handleDateClick = (date: string) => {
    setSelectedDate(date);
    onDateChange?.(date);
    setShowCalendar(false);
  };
  
  const handleResetDate = () => {
    setSelectedDate(null);
    onDateChange?.(null);
  };
  
  const requestSort = (key: 'patientName' | 'date') => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const filteredAndSortedAppointments = useMemo(() => {
    let filteredAppointments = appointments.filter(app => {
      if (searchTerm && !app.idCard.includes(searchTerm)) return false;
      if (syncFilter === 'Synced' && !app.isSynced) return false;
      if (syncFilter === 'NotSynced' && app.isSynced) return false;
      if (serviceFilter !== 'All' && app.service !== serviceFilter) return false;
      if (providerFilter !== 'All' && app.supportProvider !== providerFilter) return false;
      if (selectedDate && app.date !== selectedDate) return false;
      return true;
    });

    if (sortConfig.key) {
        return [...filteredAppointments].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            if (sortConfig.key === 'date') {
                return a.time.localeCompare(b.time);
            }
            return 0;
        });
    }
    return filteredAppointments;
  }, [appointments, searchTerm, syncFilter, serviceFilter, selectedDate, providerFilter, sortConfig]);

  const getTableTitle = () => {
    if (selectedDate) {
      return `תורים ליום ${new Date(selectedDate).toLocaleDateString('he-IL', { day: 'numeric', month: 'long' })}`;
    }
    return 'סיכום כל התורים';
  }

  return (
    <div className="p-6">
      {/* Toolbar with filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Date Picker */}
          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1">תאריך</label>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="w-48 px-4 py-2 border border-slate-300 rounded-md text-right hover:border-teal-500 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white flex items-center justify-between"
            >
              <span className={selectedDate ? 'text-slate-900' : 'text-slate-400'}>
                {selectedDate ? new Date(selectedDate).toLocaleDateString('he-IL') : 'בחר תאריך'}
              </span>
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            
            {/* Calendar Dropdown */}
            {showCalendar && (
              <div className="absolute top-full right-0 mt-2 z-50 bg-white rounded-lg shadow-2xl border border-slate-200 p-6 min-w-max">
                <div className="flex items-center justify-between mb-4 px-2">
                  <button
                    onClick={handleNext}
                    disabled={carouselIndex >= monthsToDisplay.length - 2}
                    className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handlePrev}
                    disabled={carouselIndex === 0}
                    className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  {visibleMonths.map((month, index) => (
                    <Calendar 
                      key={index}
                      date={month} 
                      selectedDate={selectedDate} 
                      appointmentDates={appointmentDates} 
                      onDateClick={handleDateClick} 
                      usSchedule={usSchedule} 
                      xraySchedule={xraySchedule} 
                    />
                  ))}
                </div>
                {selectedDate && (
                  <button 
                    onClick={handleResetDate} 
                    className="w-full mt-4 px-4 py-2 bg-teal-100 text-teal-700 text-sm font-medium rounded-md hover:bg-teal-200 transition-colors"
                  >
                    איפוס תאריך
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sync Filter */}
          <div>
            <label htmlFor="syncFilter" className="block text-sm font-medium text-slate-700 mb-1">סנכרון</label>
            <select 
              id="syncFilter"
              value={syncFilter}
              onChange={(e) => setSyncFilter(e.target.value as 'All' | 'Synced' | 'NotSynced')}
              className="w-40 px-4 py-2 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="All">הכל</option>
              <option value="Synced">מסונכרן</option>
              <option value="NotSynced">לא מסונכרן</option>
            </select>
          </div>

          {/* Service Filter */}
          <div>
            <label htmlFor="serviceFilter" className="block text-sm font-medium text-slate-700 mb-1">שירות</label>
            <select 
              id="serviceFilter"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="w-40 px-4 py-2 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="All">הכל</option>
              {services.map((service) => (
                <option key={service.id} value={service.name}>{service.name}</option>
              ))}
            </select>
          </div>

          {/* Provider Filter */}
          <div>
            <label htmlFor="providerFilterSummary" className="block text-sm font-medium text-slate-700 mb-1">קופת חולים</label>
            <select 
              id="providerFilterSummary"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value as SupportProvider | 'All')}
              className="w-40 px-4 py-2 border border-slate-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="All">הכל</option>
              <option value="כללית">כללית</option>
              <option value="מאוחדת">מאוחדת</option>
            </select>
          </div>
       </div>
      </div>
      
       {selectedDate ? (
         <TimeSlotSchedule 
            selectedDate={selectedDate}
            onAppointmentCreated={onAppointmentCreated}
            onScheduleLoaded={onScheduleLoaded}
            userName={userName}
            searchTerm={searchTerm}
            syncFilter={syncFilter}
            serviceFilter={serviceFilter}
            providerFilter={providerFilter}
         />
       ) : loading ? (
         <AppointmentsSummarySkeleton selectedDate={null} />
       ) : (
         <div className="bg-white rounded-lg shadow-md p-8 text-center">
           <h3 className="text-lg font-bold text-slate-800 mb-4">{getTableTitle()}</h3>
           <p className="text-slate-600">סה"כ {filteredAndSortedAppointments.length} תורים</p>
         </div>
       )}
    </div>
  );
};

export default AppointmentsSummary;
